import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { ErrorMessage, Formik } from 'formik';
import isArray from 'lodash/isArray';

import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicButton from 'components/BasicButton';
import FormContentLoader from 'components/FormContentLoader';
import LoadMask from 'components/LoadMask';
import BasicLabel from 'components/BasicLabel';
import BasicInput from 'components/BasicInput';
import TextEditor from 'components/TextEditor';
import PanelContainer from 'components/PanelContainer';
import FilesFieldList from 'components/FilesFieldList';
import SendTestEmailForm from 'scenes/Form/components/SendTestEmailForm';
import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';

import { FileHelper } from 'helpers/file';
import { ERROR_FIELD } from 'constants/error-fields';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation';
import { useApi } from 'hooks/useApi';
import { EmailTemplateFollowupFormValidationSchema } from 'scenes/Form/components/EmailTemplateFollowupForm/validate';
import { Normalizer } from 'scenes/Form/components/EmailTemplateFollowupForm/normalize';
import { ENDPOINT } from 'constants/endpoints';

const EmailTemplateFollowupForm = (props) => {
  const { id, onAfterSave, onClose } = props;
  const api = useApi();

  const [formData, setFormData] = useState({
    init: false,
    fields: { Name: '', Subject: '', Message: null, attachments: [] },
  });

  useEffect(() => {
    const params = id ? { id } : {};
    api.get(ENDPOINT.FORM.GET.EMAIL_TEMPLATE_FOLLOWUP, params, {
      handleError: (response) => ResponseErrorDialog({ response }),
      handleSuccess: (response) =>
        setFormData(Normalizer.fetch({ init: true, ...response })),
    });
  }, [id]);

  const handleDownloadAttach = (attachId) => {
    api.download(
      ENDPOINT.ACTION.EMAIL_TEMPLATE_FOLLOWUP.DOWNLOAD_ATTACH,
      { id, attachId },
      {
        handleError: (response) => ResponseErrorDialog({ response }),
        handleSuccess: (response) => {
          FileHelper.downloadInit(response.file, response.filename);
        },
      }
    );
  };

  const handleRemoveFile = (index, attachId, remove, values, setFieldValue) => {
    new Promise((resolve) => {
      if (attachId) {
        ConfirmDialog({
          bodyRender: 'Are you sure you want to remove this attachment?',
          dismissWhenClose: true,
          textSuccessBtn: 'Yes',
          textCancelBtn: 'No',
        }).then(() => {
          api.post(
            ENDPOINT.ACTION.EMAIL_TEMPLATE_FOLLOWUP.REMOVE_ATTACH,
            { id, attachId },
            {
              handleError: (response) => ResponseErrorDialog({ response }),
              handleSuccess: () => resolve(),
            }
          );
        });
      } else {
        resolve();
      }
    }).then(() => {
      const size =
        Number(values.attachSize) - Number(values.attachments[index].file.size);
      setFieldValue('attachSize', size, false);
      remove(index);
    });
  };

  const handleSaveError = (response, form) => {
    if (response.data && response.data.fields) {
      const obj = {};
      for (let [key, value] of Object.entries(response.data.fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      form.setErrors(obj);
    } else {
      ResponseErrorDialog(response);
    }
  };

  const handleAdd = (files, values, setFieldValue) => {
    const size =
      files.reduce((sum, a) => sum + Number(a.file.size), 0) +
      Number(values.attachSize);
    setFieldValue('attachSize', size, false);
  };

  const saveForm = async (values, form) => {
    const params = Normalizer.send(values);
    if (id) {
      params.id = id;
    }

    api.post(ENDPOINT.FORM.SAVE.EMAIL_TEMPLATE_FOLLOWUP, params, {
      handleError: (response) => {
        handleSaveError(response, form);
        form.setSubmitting(false);
      },
      handleSuccess: async ({ id }) => {
        let hasAttachError = false;
        if (values.attachments.length) {
          hasAttachError = await sendAttachments(values.attachments, id, form);
        }
        onAfterSave(id, hasAttachError);
        form.setSubmitting(false);
      },
    });
  };

  const sendTestEmail = () => {
    FormDialog({
      title: 'Send Test Email',
      modeless: false,
      compact: true,
      params: {
        data: { id },
        endpoint: ENDPOINT.ACTION.EMAIL_TEMPLATE_FOLLOWUP.SEND_TEST_EMAIL,
      },
      form: (actions, params) => (
        <SendTestEmailForm actions={actions} params={params} />
      ),
    }).then(
      () => BasicDialog({ bodyRender: 'Email has been sent successfully' }),
      (response) => ResponseErrorDialog({ response })
    );
  };

  const sendAttach = async (attach, id) =>
    new Promise((resolve, reject) => {
      if (attach.isUploaded) {
        resolve(attach);
      } else {
        api.post(
          ENDPOINT.ACTION.EMAIL_TEMPLATE_FOLLOWUP.SAVE_ATTACH,
          { file: attach.file, id },
          {
            handleError: (response) => {
              if (response.data && response.data.attachError) {
                resolve({
                  ...attach,
                  isUploaded: false,
                  error: response.data.attachError,
                });
              } else {
                reject(response);
              }
            },
            handleSuccess: (response) =>
              resolve(Normalizer.fetchFile(response)),
          }
        );
      }
    });

  const sendAttachments = async (attachments, id, form) => {
    let hasError = false;
    try {
      const result = await Promise.all(
        attachments.map((a) => sendAttach(a, id))
      );
      result.forEach((a, i) => {
        const fieldName = `attachments[${i}]`;
        if (!a.isUploaded && a.error) {
          hasError = true;
          form.setFieldError(fieldName, a.error);
        } else {
          form.setFieldValue(fieldName, a, false);
        }
      });
    } catch (err) {
      ResponseErrorDialog({ response: err });
    }

    return hasError;
  };

  return formData.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <Formik
        initialValues={formData.fields}
        onSubmit={saveForm}
        enableReinitialize
        validationSchema={EmailTemplateFollowupFormValidationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          handleReset,
          isSubmitting,
          setFieldValue,
          setFieldError,
          setFieldTouched,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="form__body"
            style={{ position: 'relative' }}
          >
            {isSubmitting && <LoadMask relative />}
            <div className="form__inner">
              <PanelContainer styles={{ padding: '0 0 15px 0' }}>
                <div
                  className="form__group form__group--limited"
                  style={{ marginBottom: '5px' }}
                >
                  <h5 className="form__subtitle">Email Template</h5>
                </div>

                <div
                  className="form__group form__group--limited"
                  style={{ marginBottom: '15px' }}
                >
                  <BasicInput
                    label="Name"
                    required={true}
                    name="Name"
                    inputType="text"
                    content={values.Name}
                    placeholder="0 characters of the 100 character limit"
                    controlFunc={handleChange}
                    isValid={(!errors.Name && touched.Name) || !touched.Name}
                    touchFunc={handleBlur}
                    labelStyles={{ fontWeight: 'bold' }}
                  />

                  <ErrorMessage
                    name="Name"
                    render={(message) => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group form__group--limited">
                  <BasicInput
                    label="Subject"
                    required={true}
                    name="Subject"
                    inputType="text"
                    content={values.Subject}
                    placeholder="0 characters of the 100 character limit"
                    controlFunc={handleChange}
                    isValid={
                      (!errors.Subject && touched.Subject) || !touched.Subject
                    }
                    touchFunc={handleBlur}
                    labelStyles={{ fontWeight: 'bold' }}
                  />

                  <ErrorMessage
                    name="Subject"
                    render={(message) => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group">
                  <BasicLabel
                    name="Message"
                    required={true}
                    text="Message"
                    styles={{
                      fontWeight: 'bold',
                      marginTop: '15px',
                    }}
                  />

                  <TextEditor
                    name="Message"
                    enableHyperlinks
                    editorState={values.Message}
                    controlFunc={setFieldValue}
                    touchFunc={setFieldTouched}
                    isValid={
                      (!errors.Message && !!touched.Message) || !touched.Message
                    }
                  />

                  <ErrorMessage
                    name="Message"
                    render={(message) => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              </PanelContainer>

              <PanelContainer styles={{ padding: '0' }}>
                <div className="form__group" style={{ marginBottom: '5px' }}>
                  <FilesFieldList
                    name="attachments"
                    title="Attachments"
                    files={values.attachments}
                    onAdd={(files) => handleAdd(files, values, setFieldValue)}
                    onRemove={(index, id, remove) =>
                      handleRemoveFile(index, id, remove, values, setFieldValue)
                    }
                    onDownloadAttach={handleDownloadAttach}
                  />
                </div>
              </PanelContainer>

              <PanelContainer styles={{ padding: '10px 0 0 0' }}>
                <div className="form__controls">
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      disabled={isSubmitting}
                      controlFunc={handleSubmit}
                      btnWidth={150}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Save"
                    />
                    <BasicButton
                      disabled={!id}
                      controlFunc={sendTestEmail}
                      btnWidth={130}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Send test email"
                    />
                    <button
                      type="button"
                      className="form__close-btn"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </BasicButtonGroup>
                </div>
              </PanelContainer>
            </div>
          </form>
        )}
      </Formik>
    </CSSTransition>
  ) : (
    <PanelContainer>
      <FormContentLoader />
    </PanelContainer>
  );
};

export default EmailTemplateFollowupForm;
